'use client'

import { useEffect, useMemo, useState } from 'react'

function readCssVar(name: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/**
 * Convierte strings tipo:
 *  - "0 0% 92%"
 *  - "0, 0%, 92%"
 *  - "hsl(0 0% 92%)"
 *  - "hsl(0, 0%, 92%)"
 *  - "0 0% 92% / 0.8"
 * a "rgba(r, g, b, a)" compatible con Stripe Elements.
 */
function hslAnyToRgba(input: string, fallback: string) {
  try {
    if (!input) return fallback

    let s = input.trim().toLowerCase()

    // si viene como hsl(...)
    s = s.replace(/^hsla?\(/, '').replace(/\)$/, '').trim()

    // soporte "h s% l% / a"
    let alpha = 1
    if (s.includes('/')) {
      const parts = s.split('/')
      s = parts[0].trim()
      const aRaw = (parts[1] || '').trim()
      const a = Number(aRaw.replace('%', '')) // por si llega "80%"
      alpha = aRaw.includes('%') ? a / 100 : (Number.isFinite(a) ? a : 1)
    }

    // tokens separados por espacios o comas
    const tokens = s.split(/[\s,]+/).filter(Boolean)
    if (tokens.length < 3) return fallback

    const h = Number(tokens[0])
    const sRaw = tokens[1]
    const lRaw = tokens[2]

    if (!Number.isFinite(h)) return fallback

    const sat = Number(sRaw.replace('%', ''))
    const lig = Number(lRaw.replace('%', ''))
    if (!Number.isFinite(sat) || !Number.isFinite(lig)) return fallback

    const S = Math.min(100, Math.max(0, sat)) / 100
    const L = Math.min(100, Math.max(0, lig)) / 100

    // HSL -> RGB
    const hh = ((h % 360) + 360) % 360
    const C = (1 - Math.abs(2 * L - 1)) * S
    const X = C * (1 - Math.abs(((hh / 60) % 2) - 1))
    const m = L - C / 2

    let r1 = 0, g1 = 0, b1 = 0
    if (hh < 60) [r1, g1, b1] = [C, X, 0]
    else if (hh < 120) [r1, g1, b1] = [X, C, 0]
    else if (hh < 180) [r1, g1, b1] = [0, C, X]
    else if (hh < 240) [r1, g1, b1] = [0, X, C]
    else if (hh < 300) [r1, g1, b1] = [X, 0, C]
    else [r1, g1, b1] = [C, 0, X]

    const r = Math.round((r1 + m) * 255)
    const g = Math.round((g1 + m) * 255)
    const b = Math.round((b1 + m) * 255)

    const aClamped = Math.min(1, Math.max(0, alpha))
    return `rgba(${r}, ${g}, ${b}, ${aClamped})`
  } catch {
    return fallback
  }
}

export function useStripeElementTheme() {
  const [vars, setVars] = useState(() => ({
    foreground: '0 0% 10%',
    mutedForeground: '0 0% 45%',
    background: '0 0% 100%',
    danger: '0 84% 60%',
  }))

  useEffect(() => {
    const pull = () => {
      setVars({
        foreground: readCssVar('--foreground') || '0 0% 10%',
        mutedForeground: readCssVar('--muted-foreground') || '0 0% 45%',
        background: readCssVar('--background') || '0 0% 100%',
        danger: readCssVar('--destructive') || '0 84% 60%',
      })
    }

    pull()

    const mo = new MutationObserver(pull)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'style'] })

    return () => mo.disconnect()
  }, [])

  return useMemo(() => {
    // ✅ Stripe-safe colors (rgba)
    const fg = hslAnyToRgba(vars.foreground, 'rgba(26, 26, 26, 1)')
    const muted = hslAnyToRgba(vars.mutedForeground, 'rgba(115, 115, 115, 1)')
    const bg = hslAnyToRgba(vars.background, 'rgba(255, 255, 255, 1)')
    const danger = hslAnyToRgba(vars.danger, 'rgba(220, 38, 38, 1)')

    return {
      key: `${vars.foreground}|${vars.mutedForeground}|${vars.background}|${vars.danger}`, // 👈 forzar re-mount
      options: {
        style: {
          base: {
            fontSize: '16px',
            color: fg,
            fontFamily: 'Inter, sans-serif',
            '::placeholder': { color: muted },
          },
          invalid: {
            color: danger,
          },
        },
      } as const,
      bg,
    }
  }, [vars])
}